import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import PageHeader from 'components/common/PageHeader';
import { getAnalytics, getNominations } from 'services/recognitionService';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);

const VALUE_NAMES = {
  1: 'Integrity',
  2: 'Teamwork',
  3: 'Determination',
  4: 'Excellence',
  5: 'Pride'
};

const PERIODS = [
  { label: 'This Week',    variant: 'success',   from: () => dayjs().startOf('week').format('YYYY-MM-DD'),    to: () => dayjs().format('YYYY-MM-DD') },
  { label: 'This Month',   variant: 'info',      from: () => dayjs().startOf('month').format('YYYY-MM-DD'),   to: () => dayjs().format('YYYY-MM-DD') },
  { label: 'This Quarter', variant: 'secondary', from: () => dayjs().startOf('quarter').format('YYYY-MM-DD'), to: () => dayjs().format('YYYY-MM-DD') },
  { label: 'This Year',    variant: 'warning',   from: () => dayjs().startOf('year').format('YYYY-MM-DD'),    to: () => dayjs().format('YYYY-MM-DD') }
];

const buildCsv = (items) => {
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const headers = ['Nominator', 'Nominee', 'Date', 'Value(s)', 'Reason', 'Department'];
  const rows = items.map(item => {
    const values = Array.isArray(item.values)
      ? item.values.map(v => VALUE_NAMES[v.valueId] ?? v.valueName ?? v).join(' | ')
      : (Array.isArray(item.valueIds)
          ? item.valueIds.map(id => VALUE_NAMES[id] ?? id).join(' | ')
          : '');
    return [
      item.nominatorName ?? '',
      item.nomineeName  ?? '',
      item.demonstratedDate ? dayjs(item.demonstratedDate).format('DD/MM/YYYY') : '',
      values,
      item.reason ?? '',
      item.nomineeDepartment ?? item.nominatorDepartment ?? ''
    ].map(escape).join(',');
  });
  return [headers.join(','), ...rows].join('\r\n');
};

const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

function StatCard({ title, value, variant = 'primary' }) {
  return (
    <Card className="h-100 text-center shadow-sm">
      <Card.Body className="py-3">
        <div className={`display-6 fw-bold text-${variant}`}>{value ?? '—'}</div>
        <div className="small text-muted mt-1">{title}</div>
      </Card.Body>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number,
  variant: PropTypes.string
};

export default function RecognitionReportsPage() {
  const [from, setFrom] = useState(dayjs().startOf('month').toDate());
  const [to, setTo] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback((fromDate, toDate) => {
    setLoading(true);
    setError('');
    const ac = new AbortController();
    getAnalytics(
      {
        from: dayjs(fromDate).format('YYYY-MM-DD'),
        to: dayjs(toDate).format('YYYY-MM-DD')
      },
      ac.signal
    )
      .then(setData)
      .catch(err => {
        if (err?.name !== 'CanceledError') setError('Failed to load analytics.');
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, []);

  useEffect(() => {
    return load(from, to);
  }, []);

  const applyPeriod = (period) => {
    const f = dayjs(period.from()).toDate();
    const t = dayjs(period.to()).toDate();
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const handleApply = () => load(from, to);

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const fromStr = dayjs(from).format('YYYY-MM-DD');
      const toStr   = dayjs(to).format('YYYY-MM-DD');

      const result = await getNominations({ from: fromStr, to: toStr });
      const items  = result.items ?? [];
      if (!items.length) {
        setError('No nominations found for the selected period.');
        return;
      }

      const csv = buildCsv(items);
      downloadCsv(csv, `nominations_${fromStr}_${toStr}.csv`);
    } catch {
      setError('Failed to download nominations.');
    } finally {
      setDownloading(false);
    }
  };

  const factoryCount = data?.byDepartment
    ?.filter(d => /production|factory|assembly|packing|warehouse|dispatch/i.test(d.department))
    .reduce((s, d) => s + d.asNominator, 0) ?? 0;
  const officeCount = data?.byDepartment
    ?.filter(d => !/production|factory|assembly|packing|warehouse|dispatch/i.test(d.department))
    .reduce((s, d) => s + d.asNominator, 0) ?? 0;

  return (
    <Container className="py-4">
      <PageHeader title="Recognition Reports" description="Analytics and nomination history." className="mb-4" />

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <div className="small text-muted mb-1">From</div>
              <DatePicker
                selected={from}
                onChange={setFrom}
                maxDate={to}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="d-block"
              />
            </div>
            <div>
              <div className="small text-muted mb-1">To</div>
              <DatePicker
                selected={to}
                onChange={setTo}
                minDate={from}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                wrapperClassName="d-block"
              />
            </div>
            <button className="btn btn-primary" onClick={handleApply} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Apply'}
            </button>
            <button className="btn btn-outline-success" onClick={handleDownload} disabled={downloading}>
              {downloading ? <Spinner animation="border" size="sm" /> : '⬇ Download CSV'}
            </button>
            <div className="d-flex gap-2">
              {PERIODS.map(p => (
                <button key={p.label} className={`btn btn-${p.variant}`} onClick={() => applyPeriod(p)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && <div className="alert alert-danger">{error}</div>}

      {data && (
        <>
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <StatCard title="Total Nominations" value={data.totalNominations} />
            </Col>
            <Col xs={6} md={3}>
              <StatCard title="Factory Submissions" value={factoryCount} variant="success" />
            </Col>
            <Col xs={6} md={3}>
              <StatCard title="Office Submissions" value={officeCount} variant="info" />
            </Col>
            <Col xs={6} md={3}>
              <StatCard title="Values Tracked" value={data.byValue?.length ?? 5} variant="warning" />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="fw-semibold">Nominations by Value</Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" hover>
                    <thead>
                      <tr>
                        <th>Value</th>
                        <th className="text-end">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.byValue ?? []).map(v => (
                        <tr key={v.valueId}>
                          <td>{VALUE_NAMES[v.valueId] ?? v.valueName}</td>
                          <td className="text-end">
                            <Badge bg="primary">{v.count}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="fw-semibold">Nominations by Department</Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" hover>
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th className="text-end">As Nominee</th>
                        <th className="text-end">As Nominator</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.byDepartment ?? []).map(d => (
                        <tr key={d.department}>
                          <td>{d.department}</td>
                          <td className="text-end">{d.asNominee}</td>
                          <td className="text-end">{d.asNominator}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold">Top Nominees</Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th className="text-end">Nominations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.topNominees ?? []).map((n, i) => (
                        <tr key={n.employeeId}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{n.name}</td>
                          <td className="text-end"><Badge bg="success">{n.count}</Badge></td>
                        </tr>
                      ))}
                      {!data.topNominees?.length && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold">Top Nominators</Card.Header>
                <Card.Body className="p-0">
                  <Table className="mb-0" hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th className="text-end">Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.topNominators ?? []).map((n, i) => (
                        <tr key={n.employeeId}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{n.name}</td>
                          <td className="text-end"><Badge bg="info">{n.count}</Badge></td>
                        </tr>
                      ))}
                      {!data.topNominators?.length && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}
